import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ruLocale from "date-fns/locale/ru";
import subWeeks from "date-fns/subWeeks";
import { memo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-use";

import { getQueryStringValue } from "services/query/query";
import useQueryString from "services/query/useQueryString";
import { selectUsersByOptions } from "store/slices/users";

import styles from "./Options.module.sass";

const Options = ({ updateData }) => {
  const [from, setFrom] = useQueryString(
    "from",
    subWeeks(new Date(), 4).toUTCString()
  );
  const [to, setTo] = useQueryString("to", new Date().toUTCString());
  const [showByTimeInterval, setShowByTimeInterval] = useQueryString(
    "showByTime",
    "day"
  );
  const [showByValue, setShowByValue] = useQueryString(
    "showByValue",
    "ban_number"
  );
  const [kyiv, toggleKyiv] = useQueryString("kyiv", true);
  const [kr, toggleKR] = useQueryString("kr", true);
  const [imageModerators, toggleImageModerators] = useQueryString(
    "image",
    false
  );
  const [imageDollarModerators, toggleImageDollarModerators] = useQueryString(
    "dollar",
    true
  );
  const [reportModerators, toggleReportModerators] = useQueryString(
    "report",
    false
  );
  const [textModerators, toggleTextModerators] = useQueryString("text", false);
  const [admins, toggleAdmins] = useQueryString("admins", false);
  const [superadmins, toggleSuperadmins] = useQueryString("superadmins", false);
  const [indonesia, toggleIndonesia] = useQueryString("indonesia", false);
  const [server, toggleServer] = useQueryString("server", false);
  const [midfingerModerators, toggleMidfingerModerators] = useQueryString("midfinger", false);
  const [removeTenMinIntervals, toggleRemoveTenMinIntervals] = useQueryString(
    "removeTenMinutes",
    true
  );
  const [removeTwentyMinIntervals, toggleRemoveTwentyMinIntervals] =
    useQueryString("removeTwentyMinutes", true);
  const [onlyUsers, setOnlyUsers] = useQueryString("onlyUsers", []);
  const location = useLocation();

  const users = useSelector(
    selectUsersByOptions({
      kyiv,
      kr,
      imageModerators,
      imageDollarModerators,
      reportModerators,
      textModerators,
      admins,
      superadmins,
      indonesia,
      server,
      midfingerModerators,
    })
  );

  useEffect(() => {
    window.parent.postMessage(
      {
        event: "paramsChanged",
        params: window.location.search.slice(1),
      },
      "*"
    );

    updateData({
      users: Array.isArray(onlyUsers)
        ? users.length && onlyUsers.length
          ? onlyUsers
              .map((userId) => users.find((user) => user.id === userId))
              .filter((user) => user !== undefined)
          : users
        : users.filter((user) => user.id === onlyUsers),
      from,
      to,
      showByTimeInterval,
      showByValue,
      removeTenMinIntervals,
      removeTwentyMinIntervals,
    });
  });

  useEffect(() => {
    const onlyUsersFromQuery =
      getQueryStringValue("onlyUsers", location.search) || [];
    if (onlyUsers.length !== onlyUsersFromQuery.length) {
      setOnlyUsers(onlyUsersFromQuery);
    }
  }, [onlyUsers, location.search, setOnlyUsers]);

  useEffect(() => {
    if (onlyUsers.length > 0 && users.length === 0) {
      setOnlyUsers([]);
    }
  }, [users, onlyUsers, setOnlyUsers]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={ruLocale}>
      <Grid container spacing={2} padding={2}>
        <Grid item>
          <DatePicker
            renderInput={(props) => <TextField {...props} />}
            label="С"
            value={from}
            onChange={(from) => setFrom(from.toUTCString())}
            mask="__.__.____"
          />
        </Grid>
        <Grid item>
          <DatePicker
            renderInput={(props) => <TextField {...props} />}
            label="По"
            value={to}
            onChange={(to) => setTo(to.toUTCString())}
            mask="__.__.____"
          />
        </Grid>
        <Grid item>
          <div className={styles.filterGroup}>
            <label className={styles.filterGroupLabel}>Показать за</label>
            <ToggleButtonGroup
              color="primary"
              className={styles.showBy}
              value={showByTimeInterval}
              exclusive
              onChange={(_, newValue) =>
                newValue !== null && setShowByTimeInterval(newValue)
              }
            >
              <ToggleButton value="hour">Час</ToggleButton>
              <ToggleButton value="day">День</ToggleButton>
              <ToggleButton value="week">Неделю</ToggleButton>
            </ToggleButtonGroup>
          </div>
        </Grid>
        <Grid item>
          <FormControl>
            <InputLabel htmlFor="show-by-value">Выбрать показатель</InputLabel>
            <Select
              labelId="show-by-value-label"
              id="show-by-value"
              value={showByValue}
              onChange={(event) => {
                const {
                  target: { value },
                } = event;

                if (value !== null) setShowByValue(value);
              }}
              input={<OutlinedInput label="Выбрать показатель" />}
              sx={{ width: 250 }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 350,
                    maxWidth: 250,
                    width: "auto",
                  },
                },
              }}
            >
              {[
                { label: "Бан", value: "ban_number" },
                { label: "% банов платящих", value: "ban_number_percent" },
                {
                  label: "% банов удаленных сервером",
                  value: "irrelevant_ban_number_percent",
                },
                { label: "Разбан", value: "unban_number" },
                { label: "% разбанов платящих", value: "unban_number_percent" },
                { label: "Простой", value: "innefective" },
                { label: "Картинки", value: "picture_number" },
                { label: "Действия", value: "mark_number" },
              ].map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <div className={styles.filterGroup}>
            <label className={styles.filterGroupLabel}>Фильтр по группе</label>
            <FormControlLabel
              control={<Checkbox />}
              label="Киев"
              checked={kyiv}
              onChange={(event) => toggleKyiv(event.target.checked)}
            />
            <FormControlLabel
              control={<Checkbox />}
              label="Кривой Рог"
              checked={kr}
              onChange={(event) => toggleKR(event.target.checked)}
            />
          </div>
        </Grid>
        <Grid item>
          <div className={styles.filterGroup}>
            <label className={styles.filterGroupLabel}>Фильтр по роли</label>
            <FormControlLabel
              control={<Checkbox />}
              label="Картинки"
              checked={imageModerators}
              onChange={(event) => toggleImageModerators(event.target.checked)}
              disabled={onlyUsers.length > 0}
            />
            <FormControlLabel
              control={<Checkbox />}
              label="($)"
              checked={imageDollarModerators}
              onChange={(event) =>
                toggleImageDollarModerators(event.target.checked)
              }
              disabled={onlyUsers.length > 0}
            />
            <FormControlLabel
              control={<Checkbox />}
              label="Жалобы"
              checked={reportModerators}
              onChange={(event) => toggleReportModerators(event.target.checked)}
              disabled={onlyUsers.length > 0}
            />
            <FormControlLabel
              control={<Checkbox />}
              label="Текст"
              checked={textModerators}
              onChange={(event) => toggleTextModerators(event.target.checked)}
              disabled={onlyUsers.length > 0}
            />
            <FormControlLabel
              control={<Checkbox />}
              label="Помечатели"
              checked={admins}
              onChange={(event) => toggleAdmins(event.target.checked)}
              disabled={onlyUsers.length > 0}
            />
            <FormControlLabel
              control={<Checkbox />}
              label="Админы"
              checked={superadmins}
              onChange={(event) => toggleSuperadmins(event.target.checked)}
              disabled={onlyUsers.length > 0}
            />
            <FormControlLabel
              control={<Checkbox />}
              label="Индонезия"
              checked={indonesia}
              onChange={(event) => toggleIndonesia(event.target.checked)}
              disabled={onlyUsers.length > 0}
            />
            <FormControlLabel
              control={<Checkbox />}
              label="Сервер"
              checked={server}
              onChange={(event) => toggleServer(event.target.checked)}
              disabled={onlyUsers.length > 0}
            />
            <FormControlLabel
              control={<Checkbox />}
              label="Средний палец"
              checked={midfingerModerators}
              onChange={(event) => toggleMidfingerModerators(event.target.checked)}
              disabled={onlyUsers.length > 0}
            />
          </div>
        </Grid>
        <Grid item>
          <div className={styles.filterGroup}>
            <label className={styles.filterGroupLabel}>Убрать</label>
            <FormControlLabel
              control={<Checkbox />}
              label="10 минут"
              checked={removeTenMinIntervals}
              onChange={(event) =>
                toggleRemoveTenMinIntervals(event.target.checked)
              }
            />
            <FormControlLabel
              control={<Checkbox />}
              label="20 минут"
              checked={removeTwentyMinIntervals}
              onChange={(event) =>
                toggleRemoveTwentyMinIntervals(event.target.checked)
              }
            />
          </div>
        </Grid>
        <Grid item>
          <FormControl>
            <InputLabel htmlFor="only-users">
              Конкретные пользователи
            </InputLabel>
            <Select
              labelId="only-users-label"
              id="only-users"
              multiple
              disabled={users.length === 0}
              value={Array.isArray(onlyUsers) ? onlyUsers : [onlyUsers]}
              onChange={(event) => {
                const {
                  target: { value },
                } = event;

                setOnlyUsers(
                  value.indexOf(-1) !== -1
                    ? []
                    : typeof value === "string"
                    ? value.split(",")
                    : value
                );
              }}
              input={<OutlinedInput label="Конкретные пользователи" />}
              sx={{ width: 250 }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 576,
                    maxWidth: 250,
                    width: "auto",
                  },
                },
              }}
            >
              {[{ id: -1, name: "Убрать всех" }, ...users]
                .map((user) => {
                  const getSuffix = (user) => {
                    switch (user.role) {
                      case "admin":
                        return "(a)";
                      case "text_moderator":
                        return "(т)";
                      case "report_moderator":
                        return "(ж)";
                      case "midfinger_moderator":
                        return "(ф)";
                      case "supermoderator":
                        return "(п)";
                      case "moderator":
                        if (user.group === "default") return "(К)";
                        else if (user.group === "anton") return "(КР)";
                        else return "";
                      default:
                        return "";
                    }
                  };
                  const name =
                    user.name.split(/\(([a-zA-Zа-я]+)\)/g)[0] + getSuffix(user);

                  return { ...user, name };
                })
                .sort((userA, userB) => {
                  if (userA.role > userB.role) {
                    return 1;
                  } else if (userB.role > userA.role) {
                    return -1;
                  } else {
                    return 0;
                  }
                })
                .sort((userA, userB) => {
                  if (
                    userA.role === "moderator" &&
                    userB.role === "moderator"
                  ) {
                    if (
                      userA.group === "default" &&
                      userB.group !== "default"
                    ) {
                      return -1;
                    } else if (
                      userB.group === "default" &&
                      userA.group !== "default"
                    ) {
                      return 1;
                    } else {
                      return 0;
                    }
                  }
                  return 0;
                })
                .sort((userA, userB) => {
                  if (
                    userA.role === userB.role &&
                    ((userA.role === "moderator" &&
                      userA.group === userB.group) ||
                      userA.role !== "moderator")
                  ) {
                    return userA.name.localeCompare(userB.name);
                  }
                  return 0;
                })
                .map((user) => (
                  <MenuItem
                    key={user.id}
                    value={user.id}
                    sx={{
                      lineHeight: 1,
                      minHeight: "28px !important",
                      fontWeight: user.id === -1 ? 700 : 400,
                    }}
                  >
                    {user.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default memo(Options);
